import { ChangeEvent, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getSelectedSection } from "redux/Metadata/selectors";
import { setSelectedSection } from "redux/Metadata/actions";
import { getEntityTypeFilter } from "redux/SingleDataset/selectors";
import {
  FilterItemCountsWithSelect,
  isValidEntityType,
  MetadataBarTypes,
  MetadataSectionTypes,
  MetadataSource,
  RightPaneMetrics,
} from "types";
import { ENTITY_TYPE_PARAMETER } from "helpers/constants/miscellaneous";
import classNames from "classnames";

import ContentHeader from "./ContentHeader";
import ContentBody from "./ContentBody";

import styles from "./style.module.scss";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";

interface ContentProps {
  title: string;
  totalCount: number;
  multiple?: boolean;
  isLoading?: boolean;
  type: MetadataBarTypes;
  data: Array<FilterItemCountsWithSelect>;
  sectionType: MetadataSectionTypes;
  id: string;
  selectedID: string | null;
  setSelectedID: any;
  sources?: MetadataSource[];
  selectedMetric?: RightPaneMetrics;
  searchDisabled?: boolean;
  getIsUnselectable?: (value: FilterItemCountsWithSelect) => boolean;
}

const Content = ({
  type,
  title,
  data,
  sectionType,
  id,
  selectedID,
  setSelectedID,
  selectedMetric,
  sources,
  isLoading = false,
  searchDisabled = false,
  getIsUnselectable,
}: ContentProps) => {
  const initialSearchSelection = !!selectedID;
  const [isSearchSelected, setIsSearchSelected] = useState(
    initialSearchSelection,
  );
  const queryParams = useAllQueryParamsFilters();

  const entityTypeFromQueryParam = isValidEntityType(
    queryParams[ENTITY_TYPE_PARAMETER],
  )
    ? queryParams[ENTITY_TYPE_PARAMETER]
    : null;
  const entityTypeFilter = useSelector((state) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam),
  );

  const selectedSection = useSelector(getSelectedSection);

  const [isDropdownOpen, setIsDropdownOpen] = useState(true);
  const contentRef = useRef<HTMLDivElement>(null);

  const dispatch = useDispatch();

  const [searchText, setSearchText] = useState("");

  const wrapperClassnames = classNames(styles.contentWrapper, {
    [styles.noHeader]: searchDisabled,
  });

  const toggleSearchSelection = () => {
    setIsSearchSelected(!isSearchSelected);
    if (!isSearchSelected) {
      setIsDropdownOpen(true);
    }

    if (selectedSection === sectionType) {
      dispatch(setSelectedSection(null));
      setSelectedID(null);
    } else if (!isSearchSelected === true) {
      //track what metadata summary section was opened

      dispatch(setSelectedSection(sectionType));
      setSelectedID(id);
    }
  };

  const selectedData = data.filter((singleValue: any) => {
    const trimmedSearchText = searchText
      .toLowerCase()
      .replace(/^\s+|\s+$/g, "");
    const title = singleValue.title?.toLowerCase();

    return title.includes(trimmedSearchText);
  });

  const showNoMatchingData = !!searchText && selectedData.length <= 0;

  const resetSection = () => {
    setIsSearchSelected(!isSearchSelected);
    setIsDropdownOpen(true);
    setSearchText("");
    dispatch(setSelectedSection(null));
    setSelectedID(null);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!!value !== !!selectedID) {
      toggleSearchSelection();
    }
    setSearchText(e.target.value);
  };

  const handleOutsideClick = (event: any) => {
    if (contentRef.current && !contentRef.current.contains(event.target)) {
      resetSection();
    }
  };

  const handleClearSearch = () => {
    if (selectedID) {
      toggleSearchSelection();
    }
    setSearchText("");
    setIsSearchSelected(false);
  };

  const renderContentHeader = () => {
    if (searchDisabled) return <noscript />;
    return (
      <ContentHeader
        title={title}
        isLoading={isLoading}
        searchText={searchText}
        showClearButton={isSearchSelected || !!searchText}
        onChange={handleSearchChange}
        onClear={handleClearSearch}
      />
    );
  };

  useEffect(() => {
    if (isSearchSelected) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
    //eslint-disable-next-line
  }, [isSearchSelected]);

  return (
    <div
      ref={contentRef}
      className={wrapperClassnames}
      style={selectedID && selectedID !== id ? { display: "none" } : undefined}
    >
      {renderContentHeader()}
      {isDropdownOpen && (
        <ContentBody
          title={title}
          type={type}
          isLoading={isLoading}
          data={searchText ? selectedData : data}
          showNoMatchingData={showNoMatchingData}
          isContentSelected={!!selectedID && selectedID === id}
          entityTypeFilter={entityTypeFilter}
          resetSection={resetSection}
          sources={sources}
          searchDisabled={searchDisabled}
          showCount={selectedMetric === "COUNT"}
          getIsUnselectable={getIsUnselectable}
        />
      )}
    </div>
  );
};

export default Content;
